/**
 * Brand directory quick-access bar: jump-to-letter scrolling, active-letter
 * tracking on scroll, and a live text filter over the brand tiles.
 */
class BrandIndexComponent extends HTMLElement {
  #jumpbar;
  #letters = [];
  #groups = [];
  #tiles = [];
  #searchInput;
  #noResults;
  #io = null;

  connectedCallback() {
    this.#jumpbar = this.querySelector('[data-brand-jumpbar]');
    this.#letters = this.#jumpbar ? [...this.#jumpbar.querySelectorAll('[data-brand-jump-letter]')] : [];
    this.#groups = [...this.querySelectorAll('[data-brand-group]')];
    this.#tiles = [...this.querySelectorAll('[data-brand-tile]')];
    this.#searchInput = this.querySelector('[data-brand-search]');
    this.#noResults = this.querySelector('[data-brand-no-results]');

    this.#letters.forEach((button) => {
      button.addEventListener('click', () => this.#jumpTo(button));
    });

    if (this.#searchInput) {
      this.#searchInput.addEventListener('input', () => this.#filter());
    }

    this.#observeGroups();
  }

  disconnectedCallback() {
    this.#io?.disconnect();
  }

  #jumpTo(button) {
    if (button.disabled) return;
    const targetId = button.dataset.target;
    const target = targetId && this.querySelector(`#${targetId}`);
    if (!target) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  #observeGroups() {
    if (!('IntersectionObserver' in window) || this.#groups.length === 0) return;

    this.#io = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (visible.length === 0) return;

        const topMost = visible.reduce((a, b) => (a.boundingClientRect.top <= b.boundingClientRect.top ? a : b));
        const letter = topMost.target.querySelector('.brand-index__group-letter')?.textContent?.trim();
        this.#setActiveLetter(letter);
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );

    this.#groups.forEach((group) => this.#io.observe(group));
  }

  #setActiveLetter(letter) {
    this.#letters.forEach((button) => {
      const isActive = letter && button.dataset.brandJumpLetter === letter;
      if (isActive) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });
  }

  #filter() {
    const query = this.#searchInput.value.trim().toLowerCase();
    let anyVisible = false;

    this.#groups.forEach((group) => {
      const tiles = [...group.querySelectorAll('[data-brand-tile]')];
      let groupHasVisible = false;

      tiles.forEach((tile) => {
        const matches = query === '' || tile.dataset.brandName.includes(query);
        tile.hidden = !matches;
        if (matches) {
          groupHasVisible = true;
          anyVisible = true;
        }
      });

      group.hidden = !groupHasVisible;
    });

    if (this.#noResults) {
      this.#noResults.hidden = anyVisible || query === '';
    }

    if (this.#jumpbar) {
      this.#jumpbar.style.visibility = query === '' ? 'visible' : 'hidden';
    }
  }
}

customElements.define('brand-index-component', BrandIndexComponent);
